import { useState } from 'react';
import { Button } from '../ui/button';
import { QrCode, Download, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const QR_URL = 'https://zuriya.co.uk/?source=qr';

const VARIANTS = [
  { key: 'bw', label: 'Black on white (most reliable)', sizes: [256, 512, 1024], svg: 'zuriya-qr.svg', preview: '/qr/zuriya-qr-512.png' },
  { key: 'brand', label: 'Branded (gold on black)', sizes: [256, 512, 1024], svg: 'zuriya-qr-brand.svg', preview: '/qr/zuriya-qr-brand-512.png' },
];

function pngHref(variant, size) {
  return variant.key === 'bw' ? `/qr/zuriya-qr-${size}.png` : `/qr/zuriya-qr-brand-${size}.png`;
}

/**
 * Admin-only QR Codes card. Shows two variants of the Zuriya QR (plain
 * black-on-white and branded gold-on-black with logo), with one-click
 * downloads at 256/512/1024 PNG plus SVG. URL encoded:
 * `https://zuriya.co.uk/?source=qr` so PostHog can attribute scans.
 */
export function QRCodesCard() {
  const [copied, setCopied] = useState(false);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(QR_URL);
      setCopied(true);
      toast.success('QR target URL copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy, please select and copy manually');
    }
  };

  return (
    <div className="card-surface p-6" data-testid="qr-codes-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-lg bg-stone-100 text-stone-700 flex items-center justify-center flex-shrink-0">
          <QrCode className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">QR Codes</h3>
          <p className="text-sm text-stone-500 mt-1">
            Print-ready QR codes for marketing materials, business cards, and shop signage.
          </p>
        </div>
      </div>

      <div className="rounded-md bg-stone-50 border border-stone-200 px-3 py-2 mb-5 flex items-center justify-between gap-3 flex-wrap">
        <code className="text-xs text-stone-700 font-mono break-all">{QR_URL}</code>
        <button
          type="button"
          onClick={copyUrl}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 px-2 py-1 rounded"
          data-testid="qr-copy-url-btn"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy URL'}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {VARIANTS.map(v => (
          <div
            key={v.key}
            className="rounded-lg border border-stone-200 bg-white p-4 flex flex-col items-center"
            data-testid={`qr-variant-${v.key}`}
          >
            <div className="w-40 h-40 mb-3 flex items-center justify-center bg-white rounded-md border border-stone-100 overflow-hidden">
              <img src={v.preview} alt={`${v.label} QR preview`} className="w-full h-full object-contain" />
            </div>
            <p className="text-xs font-medium text-[#1A1A1A] text-center mb-3">{v.label}</p>
            <div className="grid grid-cols-2 gap-1.5 w-full">
              {v.sizes.map(s => (
                <a
                  key={s}
                  href={pngHref(v, s)}
                  download
                  className="inline-flex items-center justify-center gap-1 text-[11px] font-medium px-2 py-1.5 rounded-md bg-[#1A1A1A] text-white hover:bg-stone-800 transition-colors"
                  data-testid={`qr-download-${v.key}-${s}`}
                >
                  <Download className="h-3 w-3" /> PNG {s}px
                </a>
              ))}
              <a
                href={`/qr/${v.svg}`}
                download
                className="col-span-2 inline-flex items-center justify-center gap-1 text-[11px] font-medium px-2 py-1.5 rounded-md bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#C5A028] transition-colors"
                data-testid={`qr-download-${v.key}-svg`}
              >
                <Download className="h-3 w-3" /> SVG (vector, infinite scale)
              </a>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-stone-400 mt-4">
        Tip: SVG is best for print and large signage (scales infinitely without losing quality).
        PNG 1024 is best for social posts. PNG 256 is best for thumbnails and email sigs.
        All variants encode the same URL with `?source=qr` so PostHog can attribute scans.
      </p>
    </div>
  );
}
